import { useTheme } from '@shopify/restyle';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useState } from 'react';
import type { ButtonProps, ColorValue, ModalProps } from 'react-native';
import { Modal, SectionList } from 'react-native';
import { Button } from '../button';
import type { InputProps } from '../input';
import { Input } from '../input';
import { SearchInput } from '../input/search';
import { useTranslation } from 'react-i18next';
import type { Theme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { SafeAreaView } from 'react-native-safe-area-context';

export interface Selectable<T extends number | string> {
  label: string;
  value: T;
}

export interface SelectableGroup<T extends number | string> {
  label: string;
  values: Selectable<T>[];
}

export interface SelectProps<T extends number | string>
  extends
    Pick<ButtonProps, 'disabled'>,
    Pick<InputProps, 'error' | 'label' | 'labelRight' | 'placeholder' | 'markAsRequired'> {
  onChange: (changed: Selectable<T>['value']) => void;
  options: SelectableGroup<T>[];
  value?: T | null;
  allowSearch?: boolean;
  backgroundColor?: ColorValue;
  textAlign?: 'left' | 'center';
}

export function Select<T extends number | string>({
  allowSearch = true,
  disabled,
  onChange,
  options,
  textAlign = 'left',
  value,
  ...rest
}: SelectProps<T>) {
  const theme = useTheme<Theme>();
  const [showModal, setShowModal] = useState(false);

  const selectedOption = (() => {
    for (const group of options) {
      for (const option of group.values) {
        if (option.value === value) return option;
      }
    }

    return null;
  })();

  return (
    <>
      <Input
        {...rest}
        editable={false}
        onPress={disabled ? undefined : () => setShowModal(true)}
        right={<IconChevronRight strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['border.secondary']} />}
        textAlign={textAlign}
        value={selectedOption?.label ?? value?.toString()}
      />

      <OptionsModal
        {...{ allowSearch, onChange, options }}
        key={showModal ? 'open' : 'closed'}
        onRequestClose={() => setShowModal(false)}
        visible={showModal}
      />
    </>
  );
}

function OptionsModal<T extends number | string>({
  allowSearch,
  onChange,
  onRequestClose,
  options,
  visible,
}: Pick<SelectProps<T>, 'onChange' | 'options'> &
  Pick<ModalProps, 'onRequestClose' | 'visible'> & { allowSearch: boolean }) {
  const { t } = useTranslation();

  const [search, setSearch] = useState('');

  // Filter values using search query (if any)
  const filterValue = (option: Selectable<T>) =>
    !search.trim() || option.label.toLowerCase().includes(search.toLowerCase());

  // Filter the groups, to keep only the ones that have at least one value (after search filter is applied)
  const filteredOptions = options.flatMap((group) => {
    const filteredValues = group.values.filter(filterValue);
    return filteredValues.length > 0 ? [{ key: group.label, label: group.label, data: filteredValues }] : [];
  });

  // If there are no results (all groups have been filtered out), show a message to the user
  if (filteredOptions.length === 0) {
    filteredOptions.push({
      key: 'no-results',
      label: t('noResults', { ns: 'common' }),
      data: [],
    });
  }

  return (
    <Modal {...{ onRequestClose, visible }} animationType="slide" presentationStyle="formSheet">
      <SafeAreaView style={{ flex: 1 }}>
        {allowSearch ? (
          <Column paddingHorizontal="spacing.general.x4" paddingVertical="spacing.general.x8" width="100%">
            <SearchInput initialValue={search} onSubmit={setSearch} searchAsYouType />
          </Column>
        ) : null}
        <SectionList
          sections={filteredOptions}
          keyExtractor={(item) => `${item.value.toString()}|${item.label}`}
          renderItem={({ item }) => (
            <Button
              onPress={(e) => {
                onChange(item.value);
                onRequestClose?.(e);
              }}
              paddingHorizontal="spacing.general.x4"
              paddingVertical="spacing.general.x3"
            >
              <Text fontSize={16} style={{ flex: 1 }}>
                {item.label}
              </Text>
            </Button>
          )}
          renderSectionHeader={({ section }) => (
            <Row
              backgroundColor="surface.inverse.tertiary-neutral"
              paddingHorizontal="spacing.general.x4"
              paddingVertical="spacing.general.x2"
            >
              <Text fontWeight="bold" variant="bodyBase">
                {section.label}
              </Text>
            </Row>
          )}
          stickySectionHeadersEnabled={false}
        />
      </SafeAreaView>
    </Modal>
  );
}
